import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';

// REDUX
import { useAppDispatch, useAppSelector } from '../../store';
import UserSlice from '../../store/slices/User';

// PACKAGES
import { Link, useLocation } from 'react-router-dom';
import { Alert } from 'reactstrap';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import StatusIcon from '../../components/StatusIcon';
import Button from '../../components/Button';
import DisplayDate from '../../components/DisplayDate';
import LoadingSpinner from '../../components/LoadingSpinner';

// ICONS
import { ReactComponent as PlusIcon } from '../../assets/svg/plus.svg';
import { ReactComponent as DownloadIcon } from '../../assets/svg/download.svg';
import { ReactComponent as SwitchUserIcon } from '../../assets/svg/switch-user.svg';
import { ReactComponent as SwitchIcon } from '../../assets/svg/switch.svg';

// TYPES
import { DomainProfileType, IncidentAdminDashboardIncident, IncidentUserDashboardIncident } from '../../api/models';

const IncidentReports = () => {
	const dispatch = useAppDispatch();
	const location = useLocation();

	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	// Check if Organisation is active
	const isOrganisationActive = useAppSelector((state) => state.user.isOrganisationActive);

	// Check if User is Organisation Admin
	const isOrganisationAdmin = useAppSelector((state) => state.user.isOrganisationAdmin);

	// Check if User is viewing Incident as an Admin or not
	const viewIncidentAsAdmin = useAppSelector((state) => state.user.viewIncidentAsAdmin);

	// Check Profile Type of the Active User
	const activeProfileType = useAppSelector((state) => state.user.activeProfileType);

	// Check to see if Active User is PARENT VOLUNTEER and Organisation is SPORTS
	const user = useAppSelector((state) => state.user);
	const userActiveProfileId = user.activeProfileId;
	const allProfiles = user.profiles;
	const activeProfile = allProfiles?.find((item) => item.id === userActiveProfileId);

	const isSportOrganisationParentVolunteerUser =
		activeProfileType === DomainProfileType.Parent &&
		activeProfile?.isOrganisationSportsCommunity &&
		activeProfile?.isVolunteer;

	// State to manage maximum number of items per section
	const defaultMaxItems: number = 5;

	// Set up Admin Data state and initial Admin data
	const [adminIncidentReportsData, setAdminIncidentReportsData] = React.useState<IncidentAdminDashboardIncident[]>([]);

	const getAdminIncidentReportsData = async () => {
		setIsLoading(true);

		const response = await apibridge.getAdminIncidentReportsData();

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			const { result = [] } = response;

			setIsLoading(false);

			setAdminIncidentReportsData(result);
		}
	};

	// Set up User Data state and initial User data
	const [userIncidentReportsData, setUserIncidentReportsData] = React.useState<IncidentUserDashboardIncident[]>([]);

	const getUserIncidentReportsData = async () => {
		setIsLoading(true);

		const response = await apibridge.getUserIncidentReportsData();

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			const { result = [] } = response;
			setIsLoading(false);

			setUserIncidentReportsData(result);
		}
	};

	// Check if User follow the user-view Link from Dashboard
	React.useEffect(() => {
		if (location.pathname === '/incident-reports/user-view') {
			dispatch(UserSlice.actions.update({ viewIncidentAsAdmin: false }));

			getUserIncidentReportsData();
		} else if (isOrganisationAdmin && location.pathname === '/incident-reports/admin-view') {
			dispatch(UserSlice.actions.update({ viewIncidentAsAdmin: true }));

			getAdminIncidentReportsData();
		}
	}, []);

	// Load data based on which view is loaded
	React.useEffect(() => {
		if (isOrganisationAdmin && viewIncidentAsAdmin) {
			getAdminIncidentReportsData();
		} else {
			getUserIncidentReportsData();
		}

		setIsLoading(false);
	}, []);

	// FILTER ADMIN DATA
	const newAdminIncidentReportsData = adminIncidentReportsData.filter((item) => item.isAcknowledged === false);
	const acknowledgedAdminIncidentReportsData = adminIncidentReportsData.filter((item) => item.isAcknowledged === true);

	const [maxNewAdminIncidentReports, setMaxNewAdminIncidentReports] = React.useState<number>(defaultMaxItems);
	const [maxAcknowledgedAdminIncidentReports, setMaxAcknowledgedAdminIncidentReports] =
		React.useState<number>(defaultMaxItems);

	// FILTER USER DATA
	const newUserIncidentReportsData = userIncidentReportsData.filter((item) => item.isAcknowledged === false);
	const acknowledgedUserIncidentReportsData = userIncidentReportsData.filter((item) => item.isAcknowledged === true);

	const [maxNewUserIncidentReports, setMaxNewUserIncidentReports] = React.useState<number>(defaultMaxItems);
	const [maxAcknowledgedUserIncidentReports, setMaxAcknowledgedUserIncidentReports] =
		React.useState<number>(defaultMaxItems);

	// Switch Role Function
	const switchRole = () => {
		if (isOrganisationAdmin) {
			if (viewIncidentAsAdmin) {
				dispatch(UserSlice.actions.update({ viewIncidentAsAdmin: false }));

				getUserIncidentReportsData();
			} else {
				dispatch(UserSlice.actions.update({ viewIncidentAsAdmin: true }));

				getAdminIncidentReportsData();
			}
		}
	};

	return (
		<div className="page-general-layout complaints-page">
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<>
								{isOrganisationAdmin && viewIncidentAsAdmin ? (
									<>
										<p className="sub-grey-text mbtom-30">
											Incident reports record child safety incidents – whether minor or serious – within your
											organisation. You can submit an incident report, view reports submitted by you and acknowledge
											reports assigned to you.{' '}
										</p>

										{isOrganisationAdmin && (
											<Alert color="info">
												<div className="alert-icon">
													<SwitchUserIcon />
												</div>

												<div className="alert-content">
													<p>
														Below are all incident reports submitted throughout your organisation. As a{' '}
														<strong>Leader</strong> you can view and acknowledge incident reports.
													</p>

													<p>
														You can also view incident reports <strong>submitted by you</strong> or{' '}
														<strong>assigned to you</strong> for acknowledgement. Click on ‘View my incident reports’ to
														switch roles.
													</p>
												</div>

												<Button className="btn-primary small" onClick={() => switchRole()}>
													<SwitchIcon className="link-icon" /> View my incident reports
												</Button>
											</Alert>
										)}
										{adminIncidentReportsData && adminIncidentReportsData.length > 0 ? (
											<>
												{isOrganisationActive &&
													(activeProfileType === DomainProfileType.SuperAdministrator ||
														activeProfileType === DomainProfileType.Staff) && (
														<div className="submit-btn-container">
															<Link to="/incident-submit" className="btn-primary submit-btn">
																Submit incident report
															</Link>
														</div>
													)}

												{newAdminIncidentReportsData && newAdminIncidentReportsData.length > 0 && (
													<section className="info-section incident-reports-page">
														<div className="section-header bold">
															In Progress
															<span className="quantity">{newAdminIncidentReportsData.length}</span>
														</div>

														{newAdminIncidentReportsData.slice(0, maxNewAdminIncidentReports).map((item) => {
															return (
																<div key={item.id} className="row-container">
																	<div className="row detail-row">
																		<div className="col-auto pdl-0 pdr-0">
																			<StatusIcon statusType="danger" />
																		</div>

																		<div className="col-md-auto full-width">
																			<div className="row detail-info">
																				<div className="col-lg-4 detail-name">{item.title}</div>

																				<div className="col-lg-4 detail-status">
																					<div className="title">Date Created</div>

																					<time className="name">
																						<DisplayDate>{item.created}</DisplayDate>
																					</time>
																				</div>

																				<div className="col-lg-4 detail-status">
																					<div className="title">By</div>

																					<div className="status">{item.creator}</div>
																				</div>
																			</div>
																		</div>

																		<div className="col-auto pdr-0 link-group">
																			{item.isArchivedIncident ? (
																				<Link
																					className="btn-text complaints-page"
																					to={`/incident-reports-archive/${item.id}`}
																				>
																					View
																				</Link>
																			) : (
																				<Link className="btn-text complaints-page" to={`/incident-reports/${item.id}`}>
																					View
																				</Link>
																			)}
																		</div>
																	</div>
																</div>
															);
														})}

														{maxNewAdminIncidentReports < newAdminIncidentReportsData.length && (
															<Button
																className="btn-secondary btn-show-more"
																onClick={() => setMaxNewAdminIncidentReports(newAdminIncidentReportsData.length)}
																buttonIcon={<PlusIcon className="link-icon" />}
															>
																Show {newAdminIncidentReportsData.length - maxNewAdminIncidentReports} more
															</Button>
														)}
													</section>
												)}

												{acknowledgedAdminIncidentReportsData && acknowledgedAdminIncidentReportsData.length > 0 && (
													<section className="info-section incident-reports-page">
														<div className="section-header bold">Acknowledged</div>

														{acknowledgedAdminIncidentReportsData
															.slice(0, maxAcknowledgedAdminIncidentReports)
															.map((item) => {
																return (
																	<div key={item.id} className="row-container">
																		<div className="row detail-row">
																			<div className="col-auto pdl-0 pdr-0">
																				<StatusIcon statusType="success" />
																			</div>

																			<div className="col-md-auto full-width">
																				<div className="row detail-info">
																					<div className="col-lg-4 detail-name">{item.title}</div>

																					<div className="col-lg-4 detail-status">
																						<div className="title">Date Created</div>

																						<time className="name">
																							<DisplayDate>{item.created}</DisplayDate>
																						</time>
																					</div>

																					<div className="col-lg-4 detail-status">
																						<div className="title">By</div>

																						<div className="status">{item.creator}</div>
																					</div>
																				</div>
																			</div>

																			<div className="col-auto pdr-0 link-group">
																				{item.isArchivedIncident ? (
																					<Link
																						className="btn-text complaints-page"
																						to={`/incident-reports-archive/${item.id}`}
																					>
																						View
																						<DownloadIcon className="link-icon d-block d-lg-none" />
																					</Link>
																				) : (
																					<Link
																						className="btn-text complaints-page"
																						to={`/incident-reports/${item.id}`}
																					>
																						View
																						<DownloadIcon className="link-icon d-block d-lg-none" />
																					</Link>
																				)}
																			</div>
																		</div>
																	</div>
																);
															})}

														{maxAcknowledgedAdminIncidentReports < acknowledgedAdminIncidentReportsData.length && (
															<Button
																className="btn-secondary btn-show-more"
																onClick={() =>
																	setMaxAcknowledgedAdminIncidentReports(acknowledgedAdminIncidentReportsData.length)
																}
																buttonIcon={<PlusIcon className="link-icon" />}
															>
																Show {acknowledgedAdminIncidentReportsData.length - maxAcknowledgedAdminIncidentReports}{' '}
																more
															</Button>
														)}
													</section>
												)}
											</>
										) : (
											<>
												{(activeProfileType === DomainProfileType.SuperAdministrator ||
													activeProfileType === DomainProfileType.Staff) && (
													<div className="empty-notification">
														<h2 className="info-text">
															You have not yet submitted any <strong>Incident Reports</strong>.
														</h2>

														<Link to="/incident-submit" className="btn-primary submit-btn">
															Submit incident report
														</Link>
													</div>
												)}
											</>
										)}
									</>
								) : (
									<>
										<p className="sub-grey-text mbtom-30">
											Incident reports record child safety incidents – whether minor or serious – within your
											organisation. You can submit an incident report, view reports submitted by you and acknowledge
											reports assigned to you.
										</p>

										{isOrganisationAdmin && (
											<Alert color="info">
												<div className="alert-icon">
													<SwitchUserIcon />
												</div>

												<div className="alert-content">
													<p>
														Below are incident reports <strong>submitted by you</strong> or{' '}
														<strong>assigned to you</strong> for acknowledgement.
													</p>

													<p>
														As a <strong>Leader</strong>, you can also view all incident reports submitted throughout
														your organisation. Click on ‘Manage incident reports’ to switch roles.
													</p>
												</div>

												<Button className="btn-primary small" onClick={() => switchRole()}>
													<SwitchIcon className="link-icon" /> Manage incident reports
												</Button>
											</Alert>
										)}

										{userIncidentReportsData && userIncidentReportsData.length > 0 ? (
											<>
												{isOrganisationActive &&
													(activeProfileType === DomainProfileType.SuperAdministrator ||
														activeProfileType === DomainProfileType.Staff ||
														isSportOrganisationParentVolunteerUser) && (
														<div className="submit-btn-container">
															<Link to="/incident-submit" className="btn-primary submit-btn">
																Submit incident report
															</Link>
														</div>
													)}

												{newUserIncidentReportsData && newUserIncidentReportsData.length > 0 && (
													<section className="info-section incident-reports-page">
														<div className="section-header bold">
															In Progress
															<span className="quantity">{newUserIncidentReportsData.length}</span>
														</div>

														{newUserIncidentReportsData.slice(0, maxNewUserIncidentReports).map((item) => {
															return (
																<div key={item.id} className="row-container">
																	<div className="row detail-row">
																		<div className="col-auto pdl-0 pdr-0">
																			<StatusIcon statusType="danger" />
																		</div>

																		<div className="col-md-auto full-width">
																			<div className="row detail-info">
																				<div className="col-lg-4 detail-name">{item.title}</div>

																				<div className="col-lg-4 detail-status">
																					<div className="title">Date Created</div>

																					<time className="name">
																						<DisplayDate>{item.created}</DisplayDate>
																					</time>
																				</div>

																				<div className="col-lg-4 detail-status">
																					<div className="title">By</div>

																					<div className="status">{item.creator}</div>
																				</div>
																			</div>
																		</div>

																		<div className="col-auto pdr-0 link-group">
																			{item.isArchivedIncident ? (
																				<Link
																					className="btn-text complaints-page"
																					to={`/incident-reports-archive/${item.id}`}
																				>
																					View
																				</Link>
																			) : (
																				<Link className="btn-text complaints-page" to={`/incident-reports/${item.id}`}>
																					View
																				</Link>
																			)}
																		</div>
																	</div>
																</div>
															);
														})}

														{maxNewUserIncidentReports < newUserIncidentReportsData.length && (
															<Button
																className="btn-secondary btn-show-more"
																onClick={() => setMaxNewUserIncidentReports(newUserIncidentReportsData.length)}
																buttonIcon={<PlusIcon className="link-icon" />}
															>
																Show {newUserIncidentReportsData.length - maxNewUserIncidentReports} more
															</Button>
														)}
													</section>
												)}

												{acknowledgedUserIncidentReportsData && acknowledgedUserIncidentReportsData.length > 0 && (
													<section className="info-section incident-reports-page">
														<div className="section-header bold">Acknowledged</div>

														{acknowledgedUserIncidentReportsData
															.slice(0, maxAcknowledgedUserIncidentReports)
															.map((item) => {
																return (
																	<div key={item.id} className="row-container">
																		<div className="row detail-row">
																			<div className="col-auto pdl-0 pdr-0">
																				<StatusIcon statusType="success" />
																			</div>

																			<div className="col-md-auto full-width">
																				<div className="row detail-info">
																					<div className="col-lg-4 detail-name">{item.title}</div>

																					<div className="col-lg-4 detail-status">
																						<div className="title">Date Created</div>

																						<time className="name">
																							<DisplayDate>{item.created}</DisplayDate>
																						</time>
																					</div>

																					<div className="col-lg-4 detail-status">
																						<div className="title">By</div>

																						<div className="status">{item.creator}</div>
																					</div>
																				</div>
																			</div>

																			<div className="col-auto pdr-0 link-group">
																				{item.isArchivedIncident ? (
																					<Link
																						className="btn-text complaints-page"
																						to={`/incident-reports-archive/${item.id}`}
																					>
																						View
																						<DownloadIcon className="link-icon d-block d-lg-none" />
																					</Link>
																				) : (
																					<Link
																						className="btn-text complaints-page"
																						to={`/incident-reports/${item.id}`}
																					>
																						View
																						<DownloadIcon className="link-icon d-block d-lg-none" />
																					</Link>
																				)}
																			</div>
																		</div>
																	</div>
																);
															})}

														{maxAcknowledgedUserIncidentReports < acknowledgedUserIncidentReportsData.length && (
															<Button
																className="btn-secondary btn-show-more"
																onClick={() =>
																	setMaxAcknowledgedUserIncidentReports(acknowledgedUserIncidentReportsData.length)
																}
																buttonIcon={<PlusIcon className="link-icon" />}
															>
																Show {acknowledgedUserIncidentReportsData.length - maxAcknowledgedUserIncidentReports}{' '}
																more
															</Button>
														)}
													</section>
												)}
											</>
										) : (
											<>
												{(activeProfileType === DomainProfileType.SuperAdministrator ||
													activeProfileType === DomainProfileType.Staff ||
													isSportOrganisationParentVolunteerUser) && (
													<div className="empty-notification">
														<h2 className="info-text">
															You have not yet submitted any <strong>Incident Reports</strong>.
														</h2>

														<Link to="/incident-submit" className="btn-primary submit-btn">
															Submit incident report
														</Link>
													</div>
												)}
											</>
										)}
									</>
								)}
							</>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</div>
	);
};

export default IncidentReports;
